import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {SelectInput, Text, TextInput, DateInput, Button, PhoneInput} from '@/ui-kits';
import {useFormik} from 'formik';
import {useDispatch, useSelector} from 'react-redux';
import {
  authDataSelector,
  canChangePhoneSelector,
  canChangeEmailSelector,
  authSuccessStatus,
  authFailureStatus,
  isProfileUpdateLoadingSelector,
  isPhoneVerifyLoadingSelector,
  isEmailVerifyLoadingSelector,
} from '@/services/redux/selectors';
import {Gender} from '@/constants/enums';
import {EditProfileValidationSchema} from '@/utils/schema';
import {enumToSelectOptions} from '@/utils/common_functions';
import {ProfileImageSection} from './ProfileImage';
import {
  editProfileRequest,
  profileOtpRequestRequest,
  profileOtpVerifyRequest,
} from '@/services/redux/slices/authSlice';
import {OTPVerificationModal} from './OTPVerificationModal';
import {SuccessCodes, ErrorCodes} from '@/utils/getMessages';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

interface EditProfileFormInterface {
  name: string;
  dob: Date | null;
  phone: string;
  email: string;
  weight: number | null;
  height: number | null;
  gender: Gender | null;
  address: string;
  medical_history: string;
  photo: string | null;
}

interface ProfileEditFormProps {
  onViewMode: () => void;
}
export const ProfileEditForm: React.FC<ProfileEditFormProps> = ({onViewMode}) => {
  const authData = useSelector(authDataSelector);
  const dispatch = useDispatch();

  // ===========================
  // selectors
  // ===========================
  const phoneVerifyLoading = useSelector(isPhoneVerifyLoadingSelector);
  const emailVerifyLoading = useSelector(isEmailVerifyLoadingSelector);
  const canChangeEmail = useSelector(canChangeEmailSelector);
  const canChangePhone = useSelector(canChangePhoneSelector);
  const authSuccess = useSelector(authSuccessStatus) as SuccessCodes;
  const authFailure = useSelector(authFailureStatus) as ErrorCodes;
  const isProfileUpdateLoading = useSelector(isProfileUpdateLoadingSelector);

  const initialValues = useMemo<EditProfileFormInterface>(
    () => ({
      name: authData?.username ?? '',
      phone: authData?.phone ? parseMobileNumber(authData.phone) : '',
      address: authData?.address ?? '',
      email: authData?.email_id ?? '',
      dob: authData?.dob ? new Date(authData.dob) : null,
      gender: authData?.gender ?? null,
      height: authData?.height ?? null,
      medical_history: authData?.medical_history ?? '',
      weight: authData?.weight ?? null,
      photo: authData?.photo_url ?? '',
    }),
    [authData],
  );
  const {
    values,
    dirty,
    touched,
    errors,
    handleBlur,
    isValid,
    setFieldValue,
    handleChange,
    handleSubmit,
  } = useFormik<EditProfileFormInterface>({
    initialValues,
    validationSchema: EditProfileValidationSchema,
    onSubmit,
    // enableReinitialize: true,
  });

  // ===========================
  // states
  // ===========================
  const [emailVerificationModalOpen, setEmailVerificationModalOpen] =
    useState<boolean>(false);
  const [phoneVerificationModalOpen, setPhoneVerificationModalOpen] =
    useState<boolean>(false);

  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(true);
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(true);

  // ===========================
  // functions
  // ===========================
  const isFieldEdited = useCallback((key: keyof EditProfileFormInterface) => {
    return values[key] !== initialValues[key];
  }, [values, initialValues]);

  function parseMobileNumber(phone: string): string {
    const parsed = parsePhoneNumberFromString(phone);
    const hasCountryCode = Boolean(parsed?.countryCallingCode);
    if (hasCountryCode){
      return phone
    }
    return `+91${phone}` // return indian phone number
  }

  function onSubmit(formValues: EditProfileFormInterface) {
    const formData = new FormData();
    const fieldsToAlwaysInclude: (keyof EditProfileFormInterface)[] = [
      'name',
      'email',
    ];

    // create object mapping
    Object.entries(formValues).forEach(([key, value]: [string, any]) => {
      // Handle email verification logic
      if (key === 'email') {
        // If email is edited but not verified, use the initial email value
        if (isFieldEdited('email') && !isEmailVerified) {
          formData.append(key, initialValues.email);
          return;
        }
        // If email is edited and verified, or not edited, use the current value
        formData.append(key, value);
        return;
      }
      if (key === 'phone') {
        // If phone is edited but not verified, use the initial email value
        if (isFieldEdited('phone') && !isEmailVerified) {
          formData.append(key, initialValues.email);
          return;
        }
        // If phone is edited and verified, or not edited, use the current value
        formData.append(key, value);
        return;
      }

      // skip if the value is not edited
      if (
        !fieldsToAlwaysInclude.includes(
          key as keyof EditProfileFormInterface,
        ) &&
        !isFieldEdited(key as keyof EditProfileFormInterface)
      )
        return;

      // Handle date conversion
      if (key == 'dob') {
        value = value ? value.toISOString() : '';
      }

      if (key === 'photo' && value === null) {
        formData.append(key, '');
        return;
      }

      formData.append(key, value);
    });
    dispatch(editProfileRequest(formData));
  }

  function handleGoBack() {
    onViewMode();
  }

  function initiateMobileVerify() {
    dispatch(profileOtpRequestRequest({type: 'phone', value: values.phone}));
    // const isSamePhone = initalvaluesRef.current.phone === values.phone;
    // if (!isSamePhone) {
    // } else {
    //   setPhoneVerificationModalOpen(true);
    // }
  }

  function initiateEmailVerify() {
    dispatch(profileOtpRequestRequest({type: 'email', value: values.email}));
    // const isSameEmail = initalvaluesRef.current.email === values.email;
    // if (!isSameEmail) {
    // } else {
    //   setEmailVerificationModalOpen(true);
    // }
  }

  function handleMobileVerify(otp: string) {
    dispatch(profileOtpVerifyRequest({type: 'phone', otp}));
  }

  function handleEmailVerify(otp: string) {
    dispatch(profileOtpVerifyRequest({type: 'email', otp}));
  }

  // ===========================
  // side effects
  // ===========================
  useEffect(() => {
    setIsEmailVerified(!isFieldEdited('email'));
    setIsPhoneVerified(!isFieldEdited('phone'));
  }, [values, isFieldEdited]);

  // open modal if the request is initiated
  useEffect(() => {
    if (
      authSuccess === 'S-10002' &&
      !emailVerificationModalOpen
    ) {
      setEmailVerificationModalOpen(true);
    }
    if (
      authSuccess === 'S-10031' &&
      !phoneVerificationModalOpen
    ) {
      setPhoneVerificationModalOpen(true);
    }
  }, [
    authSuccess,
    emailVerificationModalOpen,
    phoneVerificationModalOpen,
  ]);

  // close modal if the request is successful or some error occurs
  useEffect(() => {
    if (authSuccess === 'S-10024') {
      setIsPhoneVerified(true);
      setPhoneVerificationModalOpen(false);
    }

    if (authSuccess === 'S-10030') {
      setEmailVerificationModalOpen(false);
      setIsEmailVerified(true);
    }
    if (authFailure && authFailure === 'E-10069') {
      setEmailVerificationModalOpen(false);
      setPhoneVerificationModalOpen(false);
    }
  }, [authSuccess, authFailure]);


  return (
    <>
      <ProfileImageSection
        isEditing={true}
        onChange={file => setFieldValue('photo', file)}
        src={values.photo ?? ''}
      />
      <div className="shadow-[0px_4px_41.6px_-13px_rgba(0,0,0,0.15)] px-4 sm:px-6 md:px-8 lg:px-16 py-7 bg-white rounded-xl flex flex-col">
        <Text variant="title" className="!font-FigtreeSemiBold text-ui_blue mb-6">
          Personal Information
        </Text>

        <div className="grid grid-cold-1 xl:grid-cols-2 gap-x-8 3xl:gap-x-16 gap-y-8 mb-8">
          <TextInput
            label="Full Name"
            required
            value={values.name}
            error={errors.name}
            touched={touched.name}
            onChangeText={handleChange('name')}
            onBlur={handleBlur('name')}
            placeholder="Enter your full name"
            labelClassName="-mb-1"
          />
          <DateInput
            label="Date of Birth (Age)"
            value={values.dob}
            error={errors.dob as string}
            touched={touched.dob as boolean}
            onBlur={handleBlur('dob')}
            onChangeDate={date => setFieldValue('dob', date)}
            placeholder="DD-MM-YYYY"
            labelClassName="-mb-1"
          />
          <PhoneInput
            label="Phone Number"
            required
            value={values.phone ?? ''}
            error={errors.phone as string}
            disabled={Boolean(!canChangePhone)}
            touched={touched.phone}
            onChange={(phone: string) => setFieldValue('phone', phone)}
            onBlur={handleBlur('phone')}
            placeholder="Enter your phone"
            labelClassName="-mb-1"
            button={{
              text: 'Verify',
              onClick: initiateMobileVerify,
              disabled: !!(errors.phone) || isPhoneVerified,
              isLoading: phoneVerifyLoading,
            }}
          />
          <TextInput
            label="Email ID"
            required
            value={values.email}
            error={errors.email}
            touched={touched.email}
            disabled={Boolean(!canChangeEmail)}
            onChangeText={handleChange('email')}
            onBlur={handleBlur('email')}
            placeholder="Enter email"
            labelClassName="-mb-1"
            button={{
              text: 'Verify',
              onClick: initiateEmailVerify,
              disabled: !!(errors.email) || isEmailVerified,
              isLoading: emailVerifyLoading,
            }}
          />
          <TextInput
            label="Weight (kg)"
            type="numeric"
            max={3}
            value={values.weight?.toString() ?? ''}
            error={errors.weight}
            touched={touched.weight}
            onChangeText={handleChange('weight')}
            onBlur={handleBlur('weight')}
            placeholder="Enter weight"
            labelClassName="-mb-1"
          />
          <TextInput
            label="Height (cm)"
            type="numeric"
            max={3}
            value={values.height?.toString() ?? ''}
            error={errors.height}
            touched={touched.height}
            onChangeText={handleChange('height')}
            onBlur={handleBlur('height')}
            placeholder="Enter height"
            labelClassName="-mb-1"
          />
          <SelectInput
            label="Gender"
            value={values.gender}
            error={errors.gender}
            touched={touched.gender}
            options={enumToSelectOptions(Gender)}
            onChange={({id}) => setFieldValue('gender', id)}
            onBlur={handleBlur('gender')}
            placeholder="Select Your Gender"
            labelClassName="-mb-1"
            className="w-full"
            wrapperClassName="max-w-full"
          />
          <TextInput
            label="Address"
            max={200}
            value={values.address?.toString() ?? ''}
            error={errors.address}
            touched={touched.address}
            onChangeText={handleChange('address')}
            onBlur={handleBlur('address')}
            placeholder="Enter your Complete Address"
            labelClassName="-mb-1"
            preventLeadingSpace
            preventTrailingSpace
          />
        </div>

        <div className="self-center grid grid-cols-2 max-w-[400px] w-full gap-8 mt-10">
          <Button
            textClassName="font-PoppinsSemiBold"
            onClick={handleGoBack}
            title="Back"
            variant="secondary"
          />
          <Button
            textClassName="font-PoppinsSemiBold"
            onClick={() => handleSubmit()}
            title="Save Profile"
            isLoading={isProfileUpdateLoading}
            variant="primary"
            disabled={!dirty || !isValid || isProfileUpdateLoading || !isEmailVerified || !isPhoneVerified}
          />
        </div>
      </div>

      {/* OTP Modals */}
      <OTPVerificationModal
        isOpen={phoneVerificationModalOpen}
        onClose={() => setPhoneVerificationModalOpen(false)}
        onVerify={handleMobileVerify}
        type="phone"
        phoneNumber={values.phone}
      />
      <OTPVerificationModal
        isOpen={emailVerificationModalOpen}
        onClose={() => setEmailVerificationModalOpen(false)}
        onVerify={handleEmailVerify}
        type="email"
        email={values.email}
      />
    </>
  );
};
